import { apiClient } from "./client";

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  is_active: boolean;
}

export interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: "student" | "instructor" | "tenant_admin" | "superadmin";
  tenant: Tenant | null;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  /** App-level flag (không phải per-user) — Sprint 12 B5. Optional để không
   * bắt buộc mock object cũ trong test phải thêm field này. */
  feature_flags?: { live_classroom: boolean };
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await apiClient.post<LoginResponse>("/auth/login/", { email, password });
  return res.data;
}

export async function logout(refresh: string): Promise<void> {
  await apiClient.post("/auth/logout/", { refresh });
}

export async function refreshToken(refresh: string): Promise<{ access: string; refresh: string }> {
  const res = await apiClient.post<{ access: string; refresh: string }>(
    "/auth/refresh/",
    { refresh },
  );
  return res.data;
}

export async function fetchMe(): Promise<User> {
  const res = await apiClient.get<User>("/auth/me/");
  return res.data;
}

export async function forgetPassword(email: string): Promise<void> {
  await apiClient.post("/auth/forget-password/", { email });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiClient.post("/auth/reset-password/", { token, new_password: newPassword });
}

// ─── Self-signup (Sprint 5 B4) ──────────────────────────────────────────────

export interface RegisterRequest {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export async function register(payload: RegisterRequest): Promise<{ detail: string }> {
  const res = await apiClient.post<{ detail: string }>("/auth/register/", payload);
  return res.data;
}

export async function verifyEmail(token: string): Promise<{ detail: string; email?: string }> {
  const res = await apiClient.get<{ detail: string; email?: string }>(
    `/auth/verify-email/${token}/`,
  );
  return res.data;
}

// ─── Invite accept (Sprint 9 B1) ───────────────────────────────────────────

export interface InviteStatusResponse {
  email: string;
  role: User["role"];
  tenant_name: string;
  status: "valid";
}

export interface InviteAcceptRequest {
  token: string;
  password: string;
  first_name: string;
  last_name: string;
}

export type InviteAcceptResponse = LoginResponse;

/**
 * GET /api/auth/invite/status/<token>/ — preflight check invite.
 * Throws với err.response.status=404 (invalid) hoặc 410 (expired/accepted).
 */
export async function getInviteStatus(token: string): Promise<InviteStatusResponse> {
  const res = await apiClient.get<InviteStatusResponse>(`/auth/invite/status/${token}/`);
  return res.data;
}

/**
 * POST /api/auth/invite/accept/ — set password + auto-login JWT.
 * Throws với err.response.status=400 (email conflict / password yếu),
 * 404 (token invalid), 410 (expired/accepted).
 */
export async function acceptInvite(
  payload: InviteAcceptRequest,
): Promise<InviteAcceptResponse> {
  const res = await apiClient.post<InviteAcceptResponse>("/auth/invite/accept/", payload);
  return res.data;
}
